/**
 * TemplateSelector — Shows available dashboard templates as cards on the
 * analytics landing page. Clicking a template starts the board prep wizard
 * or goes directly to instantiation if boards are already ready.
 *
 * Requirements: 9.1, 9.4–9.9
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import * as api from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardTemplate {
  readonly templateId: string;
  readonly name: string;
  readonly description: string;
  readonly requiredFieldTemplateId: string;
  readonly widgets: { title: string; chartType: string }[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TemplateSelector(): React.ReactElement {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTemplates() {
      try {
        const response = await api.get<{ data: DashboardTemplate[] }>('/v1/analytics/templates/dashboards');
        setTemplates(response.data ?? []);
      } catch {
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    }
    loadTemplates();
  }, []);

  if (loading) {
    return (
      <div className="py-4">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (templates.length === 0) return <></>;

  return (
    <section aria-label="Dashboard templates" className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Create from Template</h2>
        <Button variant="outline" size="sm" onClick={() => navigate('/analytics/wizard')}>
          Prepare Board for Analytics
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {templates.map((template) => (
          <TemplateCard
            key={template.templateId}
            template={template}
            onSelect={() => navigate('/analytics/wizard')}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TemplateCard
// ---------------------------------------------------------------------------

function TemplateCard({ template, onSelect }: {
  template: DashboardTemplate;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <article
      className="flex flex-col rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:border-primary/50"
      aria-label={`Template: ${template.name}`}
    >
      <h3 className="text-sm font-semibold text-foreground">{template.name}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{template.description}</p>
      <div className="mt-3 flex flex-wrap gap-1">
        {template.widgets.map((w) => (
          <span
            key={w.title}
            className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
          >
            {w.title}
          </span>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <Button size="sm" onClick={onSelect}>
          Use Template
        </Button>
      </div>
    </article>
  );
}
